import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assignSupportToTicket, updateUserDetails, toggleAccountLock, fetchAllUsers, fetchUser } from '../../../../../slices/userSlice';
import { fetchAllTickets } from '../../../../../slices/TicketSlice';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import { toast } from 'react-toastify';
import './UserDetailedView.scss';
import { Avatar, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button } from '@mui/material';
import PersonIcon from "@mui/icons-material/Person";
import FilterRoundedIcon from '@mui/icons-material/FilterRounded';

function UserDetailedView({ selectedUser: initialUser, onClose, onSave }) {
    const dispatch = useDispatch();
    const [isPhotoModalOpen, setPhotoModalOpen] = useState(false);
    const [errors, setErrors] = useState({ profilePictureURL: "" });
    const currentUser = useSelector((state) => state.user.user); //current user
    const freshUser = useSelector((state) => 
        state.user?.usersList?.find(u => u.id === initialUser?.id)
    );
    const liveUser = currentUser?.id === initialUser?.id ? currentUser : freshUser;

    const selectedUser = React.useMemo(() => {
        if (!liveUser) return initialUser;
        
        const activeTicket = liveUser.activeTicket || initialUser?.activeTicket;
        const assignedTicket = liveUser.assignedTicket || initialUser?.assignedTicket;

        const hasFreshPhone = liveUser.phoneNumber && liveUser.phoneNumber.trim() !== "";

        const currentLockStatus = typeof liveUser.accountLocked === "boolean"
            ? liveUser.accountLocked
            : initialUser?.accountLocked;

        return {
            ...initialUser,
            ...liveUser,
            accountLocked: currentLockStatus,
            phoneNumber: hasFreshPhone ? liveUser.phoneNumber : (initialUser?.phoneNumber || ""),
            activeTicket: activeTicket,
            assignedTicket: assignedTicket,
        };
    }, [initialUser, liveUser]);

    const ticketsState = useSelector((state) => state.tickets);
    const tickets = React.useMemo(() => {
        if (!ticketsState) return [];
        const list = Array.isArray(ticketsState) ? ticketsState :
                    Array.isArray(ticketsState.ticketsList) ? ticketsState.ticketsList :
                    Array.isArray(ticketsState.tickets) ? ticketsState.tickets : [];
        return list;
    }, [ticketsState]);
    const formatPhoneNumber = (rawInput) => {
        if (!rawInput) return "";
        let cleaned = String(rawInput);
        if (cleaned.includes("Not Provided")) {
            cleaned = cleaned.replace("Not Provided", "");
        }
        cleaned = cleaned.replace(/\D/g, '');
        if (cleaned.length === 0) return "";
        if (cleaned.length <= 3) return `(${cleaned}`;
        if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    };

    const parseUserRole = (user) => {
        if(!user) return "STUDENT";
        if (typeof user.roles === "string") return user.roles.replace("ROLE_", "").toUpperCase();
        if(Array.isArray(user.roles) && typeof user.roles[0] === "string") {
            return user.roles[0].replace("ROLE_","").toUpperCase();
        }
        if (Array.isArray(user.roles) && user.roles[0]?.authority) {
            return user.roles[0].authority.replace('ROLE_', '').toUpperCase();
        }
        return 'STUDENT';
    };

    const getTargetTicketId = (user, ticketsList) => {
        if (!user || !user.id) return "";

        if (Array.isArray(ticketsList) && ticketsList.length > 0) {
            const assignedTicket = ticketsList.find(ticket => ticket.assigneeId === user.id);
            if (assignedTicket) {
                return String(assignedTicket.id);
            }
        }
        const target = user.activeTicket || user.assignedTicket;
        if (!target) return "";
        return typeof target === "object" ? String(target.id || "") : String(target);
    };

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(() => {
        if (!selectedUser) return {
            firstName: "", lastName: "", email: "", phoneNumber: "", roles: "STUDENT", accountLocked: false, assignedTicketId: "", profilePictureURL: ""
        };
        return {
            firstName: selectedUser.firstName || "",
            lastName: selectedUser.lastName || "",
            email: selectedUser.email || "",
            phoneNumber: formatPhoneNumber(selectedUser.phoneNumber),
            roles: parseUserRole(selectedUser),
            accountLocked: selectedUser.accountLocked || false,
            assignedTicketId: getTargetTicketId(selectedUser, tickets),
            profilePictureURL: selectedUser.profilePictureURL || ""
        };
    });

    const isSupport = formData.roles?.toUpperCase() === 'SUPPORT';
    const isSupportOrAdmin = formData.roles.toUpperCase() === 'ADMIN' || formData.roles?.toUpperCase() === 'SUPPORT';
    
    const selectedUserId = selectedUser?.id;
    useEffect(() => {
        if (isSupportOrAdmin) {
            dispatch(fetchAllTickets());
        }
    }, [selectedUserId, dispatch, isSupportOrAdmin]);

    useEffect(() => {
        if (selectedUser && !isEditing) {
            setFormData({
                firstName: selectedUser.firstName || "",
                lastName: selectedUser.lastName || "",
                email: selectedUser.email || "",
                phoneNumber: formatPhoneNumber(selectedUser.phoneNumber),
                roles: parseUserRole(selectedUser),
                accountLocked: selectedUser.accountLocked || false,
                assignedTicketId: getTargetTicketId(selectedUser, tickets),
                profilePictureURL: selectedUser.profilePictureURL || "",
            });

            setIsEditing(false);
            setErrors({ profilePictureURL: "" });
        }
    }, [selectedUser, tickets, isEditing]);

    useEffect(() => {
        if (selectedUser && tickets.length > 0) {
            const activeTicketId = selectedUser.activeTicket?.id
            || selectedUser.activeTicket
            || selectedUser.assignedTicket?.id
            || "";

            if (activeTicketId) {
                setFormData(prev => ({
                    ...prev,
                    assignedTicketId: String(activeTicketId)
                }));
            }
        }
    }, [tickets, selectedUser]);

    if (!selectedUser) return null;
    
    // Check if the required fields are valid (not empty or just spaces)
    const isFormValid = (formData.firstName?.trim() ?? "") !== "" && 
                        (formData.lastName?.trim() ?? "") !== "" && 
                        (formData.email?.trim() ?? "") !== "" &&
                        errors.profilePictureURL === "";

    const validateField = (name, value) => {
        if (name === "profilePictureURL" && value.trim() !== "") {
            const trimmedValue = value.trim();
            const urlPattern = /^(https?:\/\/)[^\s$.?#].[^\s]*$/i;

            if (!urlPattern.test(trimmedValue)) {
                return "Please enter a valid image URL";
            } 
            if (trimmedValue.length >= 2000) {
                return "Maximum 2000 characters allowed"
            }
        }
        return "";
    }

    const handleChange = (e) => {
        const { name, value } = e.target;

        const errorMsg = validateField(name, value);
        setErrors((prev) => ({ ...prev, [name]: errorMsg }));

        if (name === 'roles') {
            const isSelectedRoleSupportOrAdmin = value.toUpperCase() === 'SUPPORT' || value.toUpperCase() === 'ADMIN';
            setFormData((prev) => ({
                ...prev,
                roles: value,
                assignedTicketId: isSelectedRoleSupportOrAdmin ? prev.assignedTicketId : ""
            }));
        } else if (name === 'phoneNumber') {
            setFormData({ ...formData, [name]: formatPhoneNumber(value) });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    
    const handleToggleLock = () => {
        if (!isEditing) return;
        setFormData((prev) => ({
            ...prev,
            accountLocked: !prev.accountLocked
        }));
    };

    const handleSave = async () => {
        // Final guard clause before saving
        if (!isFormValid || !selectedUser?.id) return;

        let standardUpdateOccured = false;

        try {
            const { accountLocked, assignedTicketId, ...profileFields } = formData;

            // values to check if role changed
            const initialRole = parseUserRole(selectedUser);
            const didRoleChange = formData.roles.toUpperCase() !== initialRole.toUpperCase();
            
            // values to check if other fields changed
            const changedFields = [];
            // if first name changed
            if (formData.firstName?.trim() !== (selectedUser.firstName || "").trim()) {
                changedFields.push("First Name");
            }
            // if last name changed
            if (formData.lastName?.trim() !== (selectedUser.lastName || "").trim()) {
                changedFields.push("Last Name");
            }
            // if email changed
            if (formData.email?.trim() !== (selectedUser.email || "").trim()) {
                changedFields.push("Email Address");
            }
            // if phone number changed
            const currentPhone = formData.phoneNumber || "";
            const initialPhone = formatPhoneNumber(selectedUser.phoneNumber);
            if (currentPhone !== initialPhone) {
                changedFields.push("Phone Number");
            }
            // if profile picture changed
            if ((formData.profilePictureURL || "").trim() !== (selectedUser.profilePictureURL || "").trim()) {
                changedFields.push("Profile Picture");
            }
            // if role changed
            if (didRoleChange) {
                changedFields.push("Account Role");
                standardUpdateOccured = true;
            }

            // handles first name, last name, phone number, and email
            if (changedFields.length > 0) {
                const payload = {
                    ...profileFields,
                    profilePictureURL: profileFields.profilePictureURL
                };

                await dispatch(updateUserDetails({
                    userId: selectedUser.id,
                    formData: payload
                })).unwrap();

                await dispatch(fetchUser());

                setIsEditing(false);
            }

            if (changedFields.length > 0 && changedFields.length <= 2) {
                toast.success(`${changedFields.join(" and ")} updated successfully!`);
            } else if (changedFields.length >= 3) {
                toast.success(`Updated fields: ${changedFields.join(", ")}`);
            }
            

            // handles account lock
            const didLockStatusChange = Boolean(formData.accountLocked) !== Boolean(selectedUser.accountLocked);
            if (didLockStatusChange) {
                try {
                    await dispatch(toggleAccountLock({
                        userId: selectedUser.id,
                        accountLocked: formData.accountLocked,
                    })).unwrap();
                    const statusText = formData.accountLocked ? "locked" : "unlocked";
                    toast.success(`Account ${statusText}!`);
                    await dispatch(fetchAllUsers());
                } catch (lockError) {
                    toast.error(`Failed to lock account: ${lockError}`);
                    setFormData(prev => ({
                        ...prev,
                        accountLocked: selectedUser.accountLocked
                    }));
                }
            }

            const initialTicketId = getTargetTicketId(selectedUser, tickets);
            const currentTicketId = formData.assignedTicketId || "";
            const didTicketChange = currentTicketId !== initialTicketId;
        
            // handles ticket assignment
            if (isSupportOrAdmin && didTicketChange && currentTicketId.trim() !== "") {
                try {
                    await dispatch(assignSupportToTicket({
                        ticketId: String(currentTicketId),
                        supportUserId: selectedUser.id
                    })).unwrap();
                    toast.success("Ticket successfully assigned!")
                } catch (ticketError) {
                    toast.error("Ticket assignment failed:", ticketError);
                }
            }

            if (standardUpdateOccured) {
                await dispatch(fetchAllUsers());
            }

            const updatedRoleForParent = [
                { authority: `ROLE_${formData.roles.toUpperCase()}`}
            ];

            const updatedTicketValue = formData.assignedTicketId ? Number(formData.assignedTicketId) : null;

            if (onSave) {
                onSave({
                    ...selectedUser,
                    ...formData,
                    roles: updatedRoleForParent,
                    accountLocked: formData.accountLocked,
                    activeTicket: updatedTicketValue,
                    assignedTicket: { id: updatedTicketValue }
                });
            }

            setIsEditing(false);

        } catch (error) {
            const errorMsg = error?.data?.message || error?.response?.data?.message || error?.message || String(error) || "Unknown error"
            toast.error(`${errorMsg}`);
    }  
}  

    const handleCancel = () => {
        const activeTicketId = selectedUser.activeTicket?.id
            || selectedUser.activeTicket
            || selectedUser.assignedTicket?.id
            || "";

        setFormData({
            firstName: selectedUser.firstName || "",
            lastName: selectedUser.lastName || "",
            email: selectedUser.email || "",
            phoneNumber: formatPhoneNumber(selectedUser.phoneNumber) || "",
            roles: selectedUser.roles?.[0]?.authority?.replace('ROLE_', '').toUpperCase() || 'STUDENT',
            accountLocked: selectedUser.accountLocked || false,
            assignedTicketId: activeTicketId ? String(activeTicketId) : "",
            profilePictureURL: selectedUser.profilePictureURL || ""
        });
        setErrors({ profilePictureURL: "" });
        setIsEditing(false);
    };

    const handleAvatar = async (e) => {
        e.preventDefault();
        if (errors.profilePictureURL) return;
        setPhotoModalOpen(false)
    }

    const currentUserRole = parseUserRole(selectedUser);
    const isUserThemself = currentUser?.id === selectedUserId;
    const isUserAdmin = currentUserRole === "ADMIN";
    const isDropdownDisabled = isUserAdmin || isUserThemself;
    return (
        <div className="profile-overlay" onClick={onClose}>
            <div className="profile-detail-container" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="profile-detail-header">
                    <h2 className="profile-detail-title">
                        {formData.firstName} {formData.lastName}'s Profile
                    </h2>
                    <div className="profile-header-actions">
                        {!isEditing && (
                            <button className="profile-edit-trigger" onClick={() => setIsEditing(true)}  data-testid="edit-button" >
                                Edit
                            </button>
                        )}
                        <button className="profile-edit-trigger" onClick={onClose}  data-testid="close-button" >✕</button>
                    </div>
                </div>

                {/* Body */}
                <div className="profile-card-content">

                    {/* Account Status */}
                    <div className="profile-status-section">
                        <p className="module-label">Account Status</p>
                        <div className="profile-status-row">
                            <h3 className="profile-status-text">
                                Account {selectedUser.accountLocked ? 'Locked' : 'Unlocked'}
                            </h3>
                            <button
                                className="profile-lock-btn"
                                onClick={handleToggleLock}
                                disabled={!isEditing}
                                data-testid="lock-icon"
                            >
                                {(isEditing ? formData.accountLocked : selectedUser.accountLocked)
                                    ? <LockIcon sx={{ fontSize: '32px'}} />
                                    : <LockOpenIcon sx={{ fontSize: '32px' , background: 'white' }} />
                                }
                            </button>
                        </div>
                    </div>

                    {/* Profile Picture */}
                    <div 
                        className={`profile-avatar-container ${isEditing ? "editable" : ""}`}
                        onClick={() => isEditing && setPhotoModalOpen(true)}
                        style={{ cursor: isEditing ? "pointer" : "default" }}
                        data-testid="user-profile-picture"
                    >
                        <Avatar
                            className="user-avatar"
                            src={formData.profilePictureURL || undefined}
                            title={isEditing ? "Click to change profile picture" : `${formData.firstName}'s Profile Picture`}
                            sx={{ width: "70px", height: "70px", bgcolor: formData.profilePictureURL ? "transparent" : "white", border: "solid 4px #6e2e76"}}
                            data-testid="avatar-picture"
                        >
                            <PersonIcon sx={{ fontSize: "60px", color: "#6e2e76"}} />
                            <FilterRoundedIcon data-testid="profile-picture-icon" className="filter-icon" />
                        </Avatar>
                        {isEditing && <span className="avatar-edit-overlay">Change</span>}
                    </div>

                    {/* Form Grid */}
              <div className="profile-form-grid">
                        <div className="profile-input-field">
                            <label className="module-label">First Name *</label>
                            {isEditing ? (
                                <input 
                                    name="firstName" 
                                    className="profile-underlined-input" 
                                    value={formData.firstName} 
                                    placeholder='Max is 50 characters...'
                                    onChange={handleChange} 
                                    maxLength={50} 
                                    required
                                    data-testid="firstName-input"
                                />
                            ) : (
                                <span className="profile-static-value" data-testid="firstName-span" >{formData.firstName}</span>
                            )}
                        </div>

                        <div className="profile-input-field">
                            <label className="module-label">Account Role</label>
                            {isEditing ? (
                                <div className="profile-select-wrapper">
                                        <select
                                            name="roles"
                                            className="profile-underlined-input"
                                            value={formData.roles}
                                            onChange={handleChange}
                                            data-testid="userRole-select"
                                            disabled={isDropdownDisabled}
                                        >
                                            <option value="STUDENT">STUDENT</option>
                                            <option value="ADMIN">ADMIN</option>
                                            <option value="SUPPORT">SUPPORT</option>
                                        </select>
                                        <span className="profile-select-arrow" data-testid="select-arrow" >▼</span>
                                    </div>
                            ) : (
                                <span className="profile-static-value"data-testid="userRole-span" >{formData.roles}</span>
                            )}
                        </div>

                        <div className="profile-input-field">
                            <label className="module-label">Last Name *</label>
                            {isEditing ? (
                                <input 
                                    name="lastName" 
                                    className="profile-underlined-input" 
                                    value={formData.lastName} 
                                    placeholder='Max is 50 characters...'
                                    onChange={handleChange} 
                                    maxLength={50} 
                                    required 
                                    data-testid="lastName-input"
                                />
                            ) : (
                                <span className="profile-static-value" data-testid="lastName-span" >{formData.lastName}</span>
                            )}
                        </div>

                        <div className="profile-input-field">
                            <label className="module-label">Email Address</label>
                                <span className="profile-static-value"  data-testid="email-span">{formData.email}</span>
                        </div>

                        <div className="profile-input-field">
                            <label className="module-label">Phone Number</label>
                            {isEditing ? (
                                <input name="phoneNumber" className="profile-underlined-input" value={formData.phoneNumber} onChange={handleChange} maxLength={20} placeholder='Not Provided' data-testid="phone-number-input" />
                            ) : (
                                <span className="profile-static-value" data-testid="phone-number-span" >{formData.phoneNumber || "Not Provided"}</span>
                            )}
                        </div>

                    </div>

                    {/* Ticket Assignment — only visible when role is Support or Admin */}
                    {isSupportOrAdmin && (
                        <div className="profile-ticket-section">
                            <p className="module-label">Ticket Assignment</p>
                            <div className="profile-select-wrapper">
                                <select
                                    name="assignedTicketId"
                                    className="profile-underlined-input"
                                    disabled={!isEditing}
                                    value={formData.assignedTicketId}
                                    onChange={handleChange}
                                    data-testid="ticket-assignment-select"
                                >
                                    <option value="" data-testid="ticket-assignment-option" >Select a ticket...</option>
                                    {tickets
                                    .filter((ticket) => ticket.status !== "CLOSED")
                                    .map((ticket) => (
                                        <option key={ticket.id} value={String(ticket.id)}>
                                            Ticket #{ticket.id} - {ticket.title}
                                        </option>
                                    ))}
                                </select>
                                <span className="profile-select-arrow" data-testid="select-arrow">▼</span>
                            </div>
                        </div>
                    )}

                    {/* Save / Cancel — only visible when editing */}
                    {isEditing && (
                        <div className="profile-form-actions">
                            <button 
                                className={`action-btn-save ${!isFormValid ? 'disabled' : ''}`} 
                                onClick={handleSave}
                                disabled={!isFormValid}
                                data-testid="save-button"
                            >
                                Save Changes
                            </button>
                            <button className="action-btn-cancel" onClick={handleCancel}  data-testid="cancel-button" >
                                Cancel
                            </button>
                        </div>
                    )}

                </div>
            </div>

            {/* Profile Picture Editing Field */}
                <Dialog open={isPhotoModalOpen} onClose={() => {
                            setFormData(prev => ({
                                ...prev,
                                profilePictureURL: selectedUser.profilePictureURL || ""
                            }));
                            setErrors({ profilePictureURL: "" });
                            setPhotoModalOpen(false)}} className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
                    <DialogTitle>Edit Profile Picture</DialogTitle>
                    <DialogContent sx={{ minWidth: "250px", maxWidth: "300px", minHeight: "107px", pt: 1 }}>
                        <TextField
                            data-testid="update-avatar"
                            fullWidth
                            label="Image URL"
                            variant="outlined"
                            value={formData.profilePictureURL}
                            name="profilePictureURL"
                            onChange={handleChange}
                            inputProps={{ maxLength: 2000 }}
                            placeholder="https://example.com/image.jpg"
                            sx={{ mt: 1 }}
                            error={Boolean(errors.profilePictureURL)}
                            helperText={errors.profilePictureURL}
                            data-testid="profile-input-field"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setFormData(prev => ({
                                ...prev,
                                profilePictureURL: selectedUser.profilePictureURL || ""
                            }));
                            setErrors({ profilePictureURL: "" });
                            setPhotoModalOpen(false)}}>
                            Cancel
                            </Button>
                        <Button sx={{ color: "white", bgcolor: "#6e2e76", "&:hover" : { bgcolor: "#5a2561" } }} onClick={(e) => {handleAvatar(e)}} data-testid="save-avatar-url" disabled={Boolean(errors.profilePictureURL)}>Done</Button>
                    </DialogActions>
                </Dialog>
        </div>
    );
}

export default UserDetailedView;